import {z} from 'zod';

// ============================================================================
// Global Push-Notifs Live-Config Doc
// ============================================================================
// Schema, path, and read helpers for the global push-notifications live
// config doc at `global/pushNotifsOn`. Both the Cloud Functions bulk sender
// (`sendNotificationEmails` in functions/notifications/push_notifications.ts)
// and the webapp notification-settings editor
// (`NotifSettings.tsx`) read this doc, so the field names and path are
// declared here as the single source of truth.
//
// SDK-free so it can be imported into unit tests and either app without
// pulling the admin SDK (which trips deployment-safety lints).
// ============================================================================

/**
 * Firestore path of the global push-notifications live config doc.
 *
 * Use this constant instead of inlining the string so server (Functions)
 * and client (Webapp) consumers stay in lockstep — change the path here
 * and both apps follow.
 */
export const PUSH_NOTIFS_LIVE_DOC_PATH = 'global/pushNotifsOn';

/**
 * Field name on the live doc for the temporary main-forum volume cap.
 * Exported so consumers can use it as a typed key rather than restating
 * the string. See {@link PushNotifsLiveSchema}.
 */
export const SUPPRESS_MAIN_FORUM_BULK_FOR_STUDENTS_FIELD =
  'suppressMainForumBulkForStudents';

/**
 * Shape of the global push-notifications live config doc at
 * {@link PUSH_NOTIFS_LIVE_DOC_PATH}. `.loose()` so undocumented fields
 * written by other consumers pass through unchanged.
 */
export const PushNotifsLiveSchema = z
  .object({
    /**
     * Allow-list of course ids for which the bulk push-notification path is
     * live. The bulk sender short-circuits if the trigger's courseId is not
     * in this array. Optional because some envs (e.g. an empty live-config
     * doc in dev) may omit it; treat absent as empty.
     */
    courses: z.array(z.string()).optional(),
    /**
     * Temporary email-volume cap: when `true`, the bulk sender drops
     * basic-student-role recipients (`STUDENT`, `EXPERIENCED_STUDENT`) from
     * bulk fan-outs on the `main` forum. Other roles, other forums, and
     * pinpoint reply emails are unaffected. Webapp uses the same flag to
     * hide the matching toggles in the notification-settings UI so the
     * experience matches reality. Flip to `false` (or remove the field) to
     * restore previous behavior — per-user `emailLists` subscriptions stay
     * intact through the cap.
     */
    [SUPPRESS_MAIN_FORUM_BULK_FOR_STUDENTS_FIELD]: z.boolean().optional(),
  })
  .loose();

export type PushNotifsLive = z.infer<typeof PushNotifsLiveSchema>;

/**
 * Returns whether the bulk push-notification path is live for `courseId`,
 * given the parsed live-doc payload. Mirrors the existing
 * `liveDoc.data()?.courses?.includes(courseId)` check; null/undefined
 * payloads (doc missing) return `false`.
 *
 * @param params.data Parsed live-doc payload, or `undefined` when the doc
 *     doesn't exist.
 * @param params.courseId Course id to check.
 * @return `true` when the courseId is on the allow-list, else `false`.
 */
export function isPushNotifsEnabledForCourse({
  data,
  courseId,
}: {
  data: PushNotifsLive | undefined;
  courseId: string;
}): boolean {
  return Array.isArray(data?.courses) && data.courses.includes(courseId);
}

/**
 * Returns whether the main-forum bulk volume cap is currently engaged,
 * given the parsed live-doc payload. Absent field defaults to `false`
 * (cap off, pre-existing behavior).
 *
 * @param data Parsed live-doc payload, or `undefined` when the doc
 *     doesn't exist.
 * @return `true` when the cap is on, else `false`.
 */
export function isMainForumBulkSuppressedForStudents(
  data: PushNotifsLive | undefined,
): boolean {
  return data?.[SUPPRESS_MAIN_FORUM_BULK_FOR_STUDENTS_FIELD] === true;
}
